/**
 * Usuários e acessos (Configurações → Usuários) — CRUD da equipe com permissão por módulo.
 *
 * O administrador cadastra nome, e-mail e senha, escolhe o papel e, para operadores, define módulo
 * a módulo se a pessoa "não vê", "só visualiza" ou "visualiza e edita". Bloqueio rápido tira o
 * acesso na hora (o servidor recusa mesmo um login que ainda esteja aberto).
 *
 * Duas camadas, como as outras telas: `UsuariosView` é pura (recebe dados e ações, testável em
 * SSR) e `Usuarios` liga aos hooks. Os níveis e predefinições vêm de `packages/config/src/access`,
 * a mesma definição que o servidor aplica.
 */
import React, { useMemo, useState, type FormEvent, type ReactElement } from "react";
import { Ban, Eye, EyeOff, KeyRound, Pencil, ShieldCheck, Trash2, UserPlus } from "lucide-react";
import { Avatar, Badge, Button, Card, Drawer, Field, PageHeader, controlClass } from "../ui/index.js";
import { StatusPanel, errorDetail } from "../components/StatusPanel.js";
import { useStaffUserCreate, useStaffUserDelete, useStaffUserEvents, useStaffUsers, useStaffUserUpdate, useMyAccess } from "../queries.js";
import { formatDateTime } from "../format.js";
import {
  ACCESS_LEVELS,
  ACCESS_MODULES,
  ACCESS_PRESETS,
  type AccessLevel,
  type PermissionMap
} from "../../../../packages/config/src/access.js";
import {
  ACTION_LABELS,
  ROLE_LABELS,
  generatePassword,
  userErrorMessage,
  type NewStaffUserInput,
  type StaffRoleKey,
  type StaffUser,
  type StaffUserEvent,
  type StaffUserPatch
} from "../users/model.js";

/** Resumo curto do que a pessoa acessa, para a lista. */
export function accessSummary(user: StaffUser): string {
  if (user.role === "admin") return "Acesso total";
  const levels = Object.values(user.access);
  const edit = levels.filter((level) => level === "edit").length;
  const view = levels.filter((level) => level === "view").length;
  const none = levels.filter((level) => level === "none").length;
  return [edit ? `${edit} editando` : null, view ? `${view} só vendo` : null, none ? `${none} sem acesso` : null].filter(Boolean).join(" · ");
}

interface Draft {
  fullName: string;
  email: string;
  password: string;
  role: StaffRoleKey;
  permissions: PermissionMap;
}

const FULL_ACCESS: PermissionMap = ACCESS_PRESETS[0]?.permissions ?? {};

function emptyDraft(): Draft {
  return { fullName: "", email: "", password: "", role: "operator", permissions: { ...FULL_ACCESS } };
}

function draftFrom(user: StaffUser): Draft {
  return { fullName: user.fullName, email: user.email, password: "", role: user.role, permissions: { ...user.access } };
}

export interface UsuariosViewProps {
  users: StaffUser[];
  currentUserId: string | null;
  /** `null` = nenhum erro; mensagem já legível. */
  feedback: { kind: "error" | "success"; message: string } | null;
  busy: boolean;
  onCreate: (input: NewStaffUserInput) => Promise<void>;
  onUpdate: (id: string, patch: StaffUserPatch) => Promise<void>;
  onToggleBlock: (user: StaffUser) => void;
  onDelete: (user: StaffUser) => Promise<void>;
  events?: StaffUserEvent[] | null;
  onSelectForEvents?: (id: string | null) => void;
}

function statusBadge(status: StaffUser["status"]): ReactElement {
  return status === "blocked" ? <Badge tone="danger">Bloqueado</Badge> : <Badge tone="success">Ativo</Badge>;
}

function PermissionMatrix({ value, onChange, disabled }: { value: PermissionMap; onChange: (next: PermissionMap) => void; disabled: boolean }): ReactElement {
  return (
    <div className="flex flex-col gap-4" data-block="permissoes">
      <div className="flex flex-col gap-2">
        <p className="text-sm font-extrabold text-ink">Modelos prontos</p>
        <div className="flex flex-wrap gap-2">
          {ACCESS_PRESETS.map((preset) => (
            <Button key={preset.id} size="sm" disabled={disabled} title={preset.description} data-preset={preset.id} onClick={() => onChange({ ...preset.permissions })}>
              {preset.label}
            </Button>
          ))}
        </div>
        <p className="text-xs text-muted">Escolha um modelo e ajuste o que precisar abaixo.</p>
      </div>
      <div className="flex flex-col gap-2">
        {ACCESS_MODULES.map((module) => {
          const current: AccessLevel = (value[module.key] as AccessLevel | undefined) ?? "edit";
          return (
            <fieldset key={module.key} className="flex flex-col gap-2 rounded-panel border border-border p-3" data-perm-module={module.key} disabled={disabled}>
              <legend className="px-1 text-sm font-extrabold text-ink">{module.label}</legend>
              <p className="m-0 text-xs text-muted">{module.description}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                {ACCESS_LEVELS.map((option) => {
                  const checked = current === option.level;
                  return (
                    <label
                      key={option.level}
                      title={option.hint}
                      className={
                        "flex min-h-[44px] cursor-pointer items-center gap-2 rounded-control border px-3 py-2 text-sm font-semibold " +
                        (checked
                          ? option.level === "none"
                            ? "border-danger bg-danger-soft text-danger"
                            : option.level === "view"
                              ? "border-warning bg-warning-soft text-ink"
                              : "border-success bg-success-soft text-success"
                          : "border-border-strong bg-surface text-body hover:border-ink")
                      }
                    >
                      <input
                        type="radio"
                        name={`perm-${module.key}`}
                        value={option.level}
                        checked={checked}
                        data-level={option.level}
                        onChange={() => onChange({ ...value, [module.key]: option.level })}
                      />
                      <span>{option.label}</span>
                    </label>
                  );
                })}
              </div>
            </fieldset>
          );
        })}
      </div>
    </div>
  );
}

function UserEditor({
  user,
  busy,
  error,
  onClose,
  onSave,
  events
}: {
  user: StaffUser | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: Draft) => void;
  events?: StaffUserEvent[] | null;
}): ReactElement {
  const [draft, setDraft] = useState<Draft>(() => (user ? draftFrom(user) : emptyDraft()));
  const [showPassword, setShowPassword] = useState(false);
  const editing = user !== null;
  const set = <K extends keyof Draft>(key: K, value: Draft[K]): void => setDraft((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    onSave(draft);
  }

  return (
    <form className="flex flex-col gap-6" onSubmit={submit} data-form="usuario" noValidate>
      <section className="flex flex-col gap-4" data-block="dados">
        <h3 className="text-lg font-extrabold text-ink">Dados de acesso</h3>
        <Field label="Nome completo">
          {({ id }) => <input id={id} name="fullName" className={controlClass} value={draft.fullName} onChange={(e) => set("fullName", e.target.value)} autoComplete="off" required />}
        </Field>
        <Field label="E-mail (é o login)">
          {({ id }) => <input id={id} name="email" type="email" className={controlClass} value={draft.email} onChange={(e) => set("email", e.target.value)} autoComplete="off" required />}
        </Field>
        <Field label={editing ? "Nova senha (deixe em branco para manter a atual)" : "Senha"} hint="Mínimo de 8 caracteres, com letras e números.">
          {({ id, describedBy }) => (
            <div className="flex flex-wrap gap-2">
              <input
                id={id}
                name="password"
                type={showPassword ? "text" : "password"}
                className={controlClass + " min-w-0 flex-1"}
                value={draft.password}
                onChange={(e) => set("password", e.target.value)}
                aria-describedby={describedBy}
                autoComplete="new-password"
              />
              <Button aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"} onClick={() => setShowPassword((v) => !v)}>
                {showPassword ? <EyeOff className="h-5 w-5" aria-hidden /> : <Eye className="h-5 w-5" aria-hidden />}
              </Button>
              <Button
                data-action="generate-password"
                onClick={() => {
                  set("password", generatePassword());
                  setShowPassword(true);
                }}
              >
                <KeyRound className="h-5 w-5" aria-hidden /> Gerar senha
              </Button>
            </div>
          )}
        </Field>
      </section>

      <section className="flex flex-col gap-3" data-block="papel">
        <h3 className="text-lg font-extrabold text-ink">Tipo de usuário</h3>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          {(["operator", "admin"] as const).map((role) => (
            <label
              key={role}
              className={"flex cursor-pointer flex-col gap-1 rounded-panel border p-3 " + (draft.role === role ? "border-brand bg-brand-wash" : "border-border-strong hover:border-ink")}
            >
              <span className="flex items-center gap-2 text-sm font-extrabold text-ink">
                <input type="radio" name="role" value={role} checked={draft.role === role} onChange={() => set("role", role)} data-role={role} />
                {ROLE_LABELS[role]}
              </span>
              <span className="text-xs text-muted">
                {role === "admin" ? "Acesso total e gerencia usuários. Use só para quem manda no sistema." : "Acessa só o que você liberar abaixo."}
              </span>
            </label>
          ))}
        </div>
      </section>

      <section className="flex flex-col gap-3" data-block="acesso">
        <h3 className="text-lg font-extrabold text-ink">O que esta pessoa pode fazer</h3>
        {draft.role === "admin" ? (
          <p className="m-0 flex items-center gap-2 rounded-panel border border-success bg-success-soft p-3 text-sm text-ink" data-note="admin-total">
            <ShieldCheck className="h-5 w-5 shrink-0 text-success" aria-hidden /> Administrador tem acesso total a todos os módulos, inclusive usuários.
          </p>
        ) : null}
        <PermissionMatrix value={draft.permissions} onChange={(next) => set("permissions", next)} disabled={draft.role === "admin"} />
      </section>

      {error ? (
        <p role="alert" className="m-0 rounded-panel border border-danger bg-danger-soft p-3 text-sm font-semibold text-danger" data-feedback="error">
          {error}
        </p>
      ) : null}

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={busy} data-action="save-user">
          {busy ? "Salvando…" : editing ? "Salvar alterações" : "Criar usuário"}
        </Button>
        <Button onClick={onClose}>Cancelar</Button>
      </div>

      {editing ? (
        <section className="flex flex-col gap-2" data-block="historico">
          <h3 className="text-lg font-extrabold text-ink">Histórico</h3>
          {events && events.length ? (
            <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm text-body">
              {events.map((event) => (
                <li key={event.id}>
                  <strong className="text-ink">{ACTION_LABELS[event.action] ?? event.action}</strong> por {event.actor} · {formatDateTime(event.at)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-sm text-muted">Sem eventos registrados.</p>
          )}
        </section>
      ) : null}
    </form>
  );
}

export function UsuariosView(props: UsuariosViewProps): ReactElement {
  const { users, currentUserId, feedback, busy } = props;
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<"all" | "active" | "blocked">("all");
  const [editor, setEditor] = useState<{ mode: "create" } | { mode: "edit"; user: StaffUser } | null>(null);
  const [toDelete, setToDelete] = useState<StaffUser | null>(null);
  const [formError, setFormError] = useState<string | null>(null);

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return users.filter((user) => (statusFilter === "all" || user.status === statusFilter) && (!needle || `${user.fullName} ${user.email}`.toLowerCase().includes(needle)));
  }, [users, query, statusFilter]);

  const blockedCount = users.filter((user) => user.status === "blocked").length;

  function closeEditor(): void {
    setEditor(null);
    setFormError(null);
    props.onSelectForEvents?.(null);
  }

  async function save(draft: Draft): Promise<void> {
    setFormError(null);
    try {
      if (editor?.mode === "edit") {
        const user = editor.user;
        const patch: StaffUserPatch = {};
        if (draft.fullName !== user.fullName) patch.fullName = draft.fullName;
        if (draft.email !== user.email) patch.email = draft.email;
        if (draft.password) patch.password = draft.password;
        if (draft.role !== user.role) patch.role = draft.role;
        if (draft.role === "operator" && JSON.stringify(draft.permissions) !== JSON.stringify(user.access)) patch.permissions = draft.permissions;
        if (!Object.keys(patch).length) return closeEditor();
        await props.onUpdate(user.id, patch);
      } else {
        await props.onCreate({ fullName: draft.fullName, email: draft.email, password: draft.password, role: draft.role, permissions: draft.role === "admin" ? {} : draft.permissions });
      }
      closeEditor();
    } catch (error) {
      setFormError(userErrorMessage(error instanceof Error ? error : new Error("unknown")));
    }
  }

  return (
    <div className="page page--usuarios flex flex-col gap-6" data-page="usuarios">
      <PageHeader
        title="Usuários e acessos"
        description="Cadastre a equipe, defina o que cada pessoa vê e edita, e bloqueie um acesso na hora quando precisar."
        actions={
          <Button variant="primary" data-action="new-user" onClick={() => setEditor({ mode: "create" })}>
            <UserPlus className="h-5 w-5" aria-hidden /> Novo usuário
          </Button>
        }
      />

      {feedback ? (
        <p
          role={feedback.kind === "error" ? "alert" : "status"}
          data-feedback={feedback.kind}
          className={"m-0 rounded-panel border p-3 text-sm font-semibold " + (feedback.kind === "error" ? "border-danger bg-danger-soft text-danger" : "border-success bg-success-soft text-success")}
        >
          {feedback.message}
        </p>
      ) : null}

      <Card data-panel="usuarios">
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input
            type="search"
            className={controlClass + " max-w-sm"}
            placeholder="Buscar por nome ou e-mail"
            aria-label="Buscar usuário"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <select className={controlClass + " max-w-[200px]"} aria-label="Filtrar por situação" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
            <option value="all">Todos</option>
            <option value="active">Ativos</option>
            <option value="blocked">Bloqueados</option>
          </select>
          <p className="m-0 text-sm text-muted" data-kpi="usuarios-total">
            {users.length} {users.length === 1 ? "usuário" : "usuários"}
            {blockedCount ? ` · ${blockedCount} bloqueado${blockedCount === 1 ? "" : "s"}` : ""}
          </p>
        </div>

        {visible.length === 0 ? (
          <StatusPanel kind="empty" title={users.length ? "Nenhum usuário com esse filtro" : "Nenhum usuário cadastrado"} />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm" data-table="usuarios">
              <thead>
                <tr className="border-b border-border text-xs uppercase text-muted">
                  <th className="px-3 py-2">Pessoa</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2">Situação</th>
                  <th className="px-3 py-2">Acesso</th>
                  <th className="px-3 py-2">Último acesso</th>
                  <th className="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {visible.map((user) => {
                  const self = user.id === currentUserId;
                  return (
                    <tr key={user.id} className="border-b border-border align-middle" data-user-row={user.email} data-user-status={user.status}>
                      <td className="px-3 py-3">
                        <div className="flex items-center gap-3">
                          <Avatar name={user.fullName || user.email} />
                          <div className="min-w-0">
                            <p className="m-0 font-extrabold text-ink">
                              {user.fullName || "(sem nome)"} {self ? <Badge tone="info">você</Badge> : null}
                            </p>
                            <p className="m-0 truncate text-xs text-muted">{user.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-3">
                        <Badge tone={user.role === "admin" ? "brand" : "neutral"}>{ROLE_LABELS[user.role]}</Badge>
                      </td>
                      <td className="px-3 py-3">{statusBadge(user.status)}</td>
                      <td className="px-3 py-3 text-body">{accessSummary(user)}</td>
                      <td className="px-3 py-3 text-body">{user.lastSignInAt ? formatDateTime(user.lastSignInAt) : "Nunca entrou"}</td>
                      <td className="px-3 py-3">
                        <div className="flex flex-wrap justify-end gap-2">
                          <Button
                            size="sm"
                            data-action="edit-user"
                            onClick={() => {
                              setEditor({ mode: "edit", user });
                              props.onSelectForEvents?.(user.id);
                            }}
                          >
                            <Pencil className="h-4 w-4" aria-hidden /> Editar
                          </Button>
                          <Button size="sm" data-action="toggle-block" disabled={self || busy} title={self ? "Você não pode bloquear a própria conta" : undefined} onClick={() => props.onToggleBlock(user)}>
                            <Ban className="h-4 w-4" aria-hidden /> {user.status === "blocked" ? "Desbloquear" : "Bloquear"}
                          </Button>
                          <Button size="sm" variant="danger" data-action="delete-user" disabled={self || busy} title={self ? "Você não pode excluir a própria conta" : undefined} onClick={() => setToDelete(user)}>
                            <Trash2 className="h-4 w-4" aria-hidden /> Excluir
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Drawer open={editor !== null} title={editor?.mode === "edit" ? `Editar ${editor.user.fullName || editor.user.email}` : "Novo usuário"} onClose={closeEditor}>
        {editor ? (
          <UserEditor
            key={editor.mode === "edit" ? editor.user.id : "new"}
            user={editor.mode === "edit" ? editor.user : null}
            busy={busy}
            error={formError}
            onClose={closeEditor}
            onSave={(draft) => void save(draft)}
            events={props.events}
          />
        ) : null}
      </Drawer>

      <Drawer open={toDelete !== null} title="Excluir usuário" onClose={() => setToDelete(null)}>
        {toDelete ? (
          <div className="flex flex-col gap-4" data-block="confirmar-exclusao">
            <p className="m-0 text-base text-ink">
              Excluir <strong>{toDelete.fullName || toDelete.email}</strong> ({toDelete.email}) apaga a conta de forma definitiva. A pessoa perde o acesso na hora e isso não pode ser desfeito.
            </p>
            <p className="m-0 text-sm text-muted">Se só quiser suspender o acesso por um tempo, use “Bloquear”.</p>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="danger"
                data-action="confirm-delete"
                disabled={busy}
                onClick={() => {
                  const target = toDelete;
                  void props.onDelete(target).finally(() => setToDelete(null));
                }}
              >
                Excluir definitivamente
              </Button>
              <Button onClick={() => setToDelete(null)}>Cancelar</Button>
            </div>
          </div>
        ) : null}
      </Drawer>
    </div>
  );
}

export function Usuarios(): ReactElement {
  const users = useStaffUsers();
  const me = useMyAccess();
  const create = useStaffUserCreate();
  const update = useStaffUserUpdate();
  const remove = useStaffUserDelete();
  const [selected, setSelected] = useState<string | null>(null);
  const events = useStaffUserEvents(selected);
  const [feedback, setFeedback] = useState<UsuariosViewProps["feedback"]>(null);

  if (users.isLoading) return <StatusPanel kind="loading" title="Carregando usuários" />;
  if (users.isError) {
    const forbidden = /admin_role_required|403/.test(users.error.message);
    return <StatusPanel kind="error" title={forbidden ? "Só administradores gerenciam usuários" : "Não foi possível carregar os usuários"} detail={forbidden ? "Peça a um administrador para ajustar seu acesso." : errorDetail(users.error)} />;
  }

  const wrap = async (action: () => Promise<unknown>, success: string): Promise<void> => {
    setFeedback(null);
    try {
      await action();
      setFeedback({ kind: "success", message: success });
    } catch (error) {
      const message = userErrorMessage(error instanceof Error ? error : new Error("unknown"));
      setFeedback({ kind: "error", message });
      throw error;
    }
  };

  const busy = create.isPending || update.isPending || remove.isPending;
  return (
    <UsuariosView
      users={users.data ?? []}
      currentUserId={me.data?.id ?? null}
      feedback={feedback}
      busy={busy}
      events={selected ? events.data ?? null : null}
      onSelectForEvents={setSelected}
      onCreate={async (input) => wrap(() => create.mutateAsync(input), `Usuário ${input.fullName} criado. Passe o e-mail e a senha para a pessoa.`)}
      onUpdate={async (id, patch) => wrap(() => update.mutateAsync({ id, patch }), "Alterações salvas.")}
      onToggleBlock={(user) =>
        void wrap(() => update.mutateAsync({ id: user.id, patch: { status: user.status === "blocked" ? "active" : "blocked" } }), user.status === "blocked" ? `${user.fullName || user.email} foi desbloqueado.` : `${user.fullName || user.email} foi bloqueado e perdeu o acesso.`).catch(() => undefined)
      }
      onDelete={async (user) => wrap(() => remove.mutateAsync(user.id), `${user.fullName || user.email} foi excluído.`).catch(() => undefined)}
    />
  );
}
